// planes.js — visualizes XRPlane (ARCore plane-detection) results as
// color-coded outlines in the AR scene.
//
// Stage 1 of the procedural-ruin pipeline: just *show* what ARCore
// detects so we can confirm it's seeing the room before we build the
// ruin generator on top.
//
// Color code:
//   green   — horizontal plane (floor / table top)
//   blue    — vertical plane (wall)
//   red     — orientation unknown (rare; treat as debug)

import * as THREE from 'three'
import { Line2 } from 'three/addons/lines/Line2.js'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineGeometry } from 'three/addons/lines/LineGeometry.js'

const HORIZONTAL_COLOR = 0x44ff66
const VERTICAL_COLOR = 0x4488ff
const UNKNOWN_COLOR = 0xff4444
const LINE_WIDTH_PX = 4

// Classification by world-space normal Y component:
// - |Ny| > 0.7  → horizontal (floor / table top — normal points up/down)
// - |Ny| < 0.3  → vertical (wall — normal is roughly horizontal)
// - between     → 'unknown', mostly transient as ARCore is settling
const HORIZONTAL_NORMAL_Y_MIN = 0.7
const VERTICAL_NORMAL_Y_MAX = 0.3

let _scene = null
let _planeGroup = null
// Map<XRPlane, { mesh, lastChangedTime }>
let _meshes = new Map()

export function initPlanes(scene) {
  _scene = scene
  _planeGroup = new THREE.Group()
  _planeGroup.name = 'detected-planes'
  scene.add(_planeGroup)
  _meshes = new Map()
}

// Call from the AR animation loop. Cheap when nothing changes (just iterates
// the detected-planes set and refreshes pose matrices).
export function updatePlanes(frame, localSpace) {
  if (!_planeGroup) return
  // Older Chrome / non-supporting browsers won't expose detectedPlanes.
  if (!frame || !frame.detectedPlanes) return

  const seen = new Set()

  for (const plane of frame.detectedPlanes) {
    seen.add(plane)
    const cached = _meshes.get(plane)

    if (cached && cached.lastChangedTime >= plane.lastChangedTime) {
      // Polygon hasn't changed; refresh the world transform + reclassify
      // by computed normal in case the plane is settling.
      updateMeshPose(cached, plane, frame, localSpace)
      continue
    }

    if (cached) {
      _planeGroup.remove(cached.mesh)
      cached.mesh.geometry.dispose()
      cached.mesh.material.dispose()
    }

    const isNewPlane = !cached

    const mesh = buildPlaneMesh(plane)
    if (!mesh) continue

    _planeGroup.add(mesh)
    const cachedEntry = {
      mesh,
      lastChangedTime: plane.lastChangedTime,
      kind: 'unknown',
      normalY: 0
    }
    updateMeshPose(cachedEntry, plane, frame, localSpace)
    _meshes.set(plane, cachedEntry)

    // Log only the first time we see a given XRPlane object — skip the
    // 60Hz spam that comes from ARCore continuously refining polygons.
    if (isNewPlane) {
      const n = cachedEntry.normal || { x: 0, y: 0, z: 0 }
      console.info(
        `[planes] new plane: ARCore='${plane.orientation || '?'}' | normal=(${n.x.toFixed(2)},${n.y.toFixed(2)},${n.z.toFixed(2)}) → '${cachedEntry.kind}' | ${plane.polygon.length} verts`
      )
    }
  }

  // Reap planes that ARCore has dropped.
  for (const [plane, cached] of _meshes) {
    if (!seen.has(plane)) {
      _planeGroup.remove(cached.mesh)
      cached.mesh.geometry.dispose()
      cached.mesh.material.dispose()
      _meshes.delete(plane)
    }
  }
}

// Show/hide the plane visualization group (used to declutter once the
// ruin is built — keeps the underlying detection running, just hides
// the outlines).
export function setVisible(visible) {
  if (_planeGroup) _planeGroup.visible = visible
}

export function clearPlanes() {
  if (!_planeGroup) return
  for (const cached of _meshes.values()) {
    _planeGroup.remove(cached.mesh)
    cached.mesh.geometry.dispose()
    cached.mesh.material.dispose()
  }
  _meshes.clear()
}

// Returns the live list of XRPlane objects currently being tracked.
// Order is map-iteration order (effectively insertion order), which is
// stable across frames so consumers can do their own caching.
export function getDetectedPlanes() {
  return Array.from(_meshes.keys())
}

export function planeCounts() {
  let h = 0
  let v = 0
  let other = 0
  for (const cached of _meshes.values()) {
    if (cached.kind === 'horizontal') h++
    else if (cached.kind === 'vertical') v++
    else other++
  }
  return { horizontal: h, vertical: v, other, total: _meshes.size }
}

export function onResize(width, height) {
  for (const cached of _meshes.values()) {
    cached.mesh.material.resolution.set(width, height)
  }
}

function buildPlaneMesh(plane) {
  if (!plane.polygon || plane.polygon.length < 2) return null

  // Polygon points are in plane.planeSpace (plane-local). We render with
  // the plane's pose set as the mesh's matrix, so positions stay
  // plane-local in the geometry.
  const flat = []
  for (const p of plane.polygon) {
    flat.push(p.x, p.y, p.z)
  }
  // Close the loop by repeating the first vertex.
  const first = plane.polygon[0]
  flat.push(first.x, first.y, first.z)

  const geometry = new LineGeometry()
  geometry.setPositions(flat)

  // Initial color is the unknown color; updateMeshPose immediately
  // reclassifies based on actual world-space normal.
  const material = new LineMaterial({
    color: UNKNOWN_COLOR,
    linewidth: LINE_WIDTH_PX,
    worldUnits: false,
    depthTest: false,
    transparent: true,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight)
  })

  const mesh = new Line2(geometry, material)
  mesh.renderOrder = 400
  // Pose-driven mesh — Line2's bounding sphere doesn't account for the
  // pose matrix, so disable culling to be safe.
  mesh.frustumCulled = false
  mesh.matrixAutoUpdate = false
  mesh.computeLineDistances()
  return mesh
}

function updateMeshPose(cached, plane, frame, localSpace) {
  const pose = frame.getPose(plane.planeSpace, localSpace)
  if (!pose) return

  cached.mesh.matrix.fromArray(pose.transform.matrix)
  cached.mesh.matrixWorldNeedsUpdate = true

  // The plane's normal is the +Y axis of its planeSpace frame, in world
  // (localSpace) coords. Use the quaternion directly rather than reading
  // matrix indices — fewer assumptions about layout / scale.
  const q = pose.transform.orientation
  const quat = _scratchQuat.set(q.x, q.y, q.z, q.w)
  const normal = _scratchVec.set(0, 1, 0).applyQuaternion(quat)
  cached.normal = { x: normal.x, y: normal.y, z: normal.z }
  cached.normalY = normal.y

  const newKind = classifyByNormalY(cached.normalY)
  if (newKind !== cached.kind) {
    cached.kind = newKind
    cached.mesh.material.color.setHex(colorForKind(newKind))
    cached.mesh.material.needsUpdate = true
  }
}

const _scratchQuat = new THREE.Quaternion()
const _scratchVec = new THREE.Vector3()

function classifyByNormalY(ny) {
  const abs = Math.abs(ny)
  if (abs >= HORIZONTAL_NORMAL_Y_MIN) return 'horizontal'
  if (abs <= VERTICAL_NORMAL_Y_MAX) return 'vertical'
  return 'unknown'
}

function colorForKind(kind) {
  if (kind === 'horizontal') return HORIZONTAL_COLOR
  if (kind === 'vertical') return VERTICAL_COLOR
  return UNKNOWN_COLOR
}
